import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, List, Icon, Message, Segment } from 'semantic-ui-react';
import { StyledModal } from './SharedStyles';
import * as api from '../services/api';
import { endpoint } from '../config';

const ApiKeyModal = ({ open, onClose }) => {
  const [keys, setKeys] = useState([]);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKey, setNewKey] = useState(null);
  const [copied, setCopied] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  useEffect(() => {
    if (open) {
      loadKeys();
      setNewKey(null);
      setCopied(false);
    }
  }, [open]);

  const loadKeys = async () => {
    try {
      const data = await api.getApiKeys();
      setKeys(data);
    } catch (err) {
      console.error('Error loading API keys:', err);
    }
  };

  const handleGenerate = async () => {
    try {
      const result = await api.createApiKey(newKeyName.trim());
      setNewKey(result.key);
      setNewKeyName('');
      setCopied(false);
      loadKeys();
    } catch (err) {
      console.error('Error generating API key:', err);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(newKey);
    setCopied(true);
  };

  const handleDelete = async (id) => {
    try {
      await api.deleteApiKey(id);
      setConfirmDeleteId(null);
      loadKeys();
    } catch (err) {
      console.error('Error deleting API key:', err);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString();
  };

  const webhookUrl = `${endpoint}/api/webhook/tasks`;

  return (
    <StyledModal open={open} onClose={onClose}>
      <Modal.Header>API Keys</Modal.Header>
      <Modal.Content>
        <Segment secondary style={{ borderRadius: '12px', marginBottom: '1em' }}>
          <p style={{ margin: 0 }}>
            <strong>Webhook URL:</strong>{' '}
            <code style={{ wordBreak: 'break-all' }}>{webhookUrl}</code>
          </p>
          <p style={{ margin: '0.5em 0 0', color: '#666', fontSize: '0.9em' }}>
            Use with IFTTT Webhooks, Zapier, or any HTTP client. Include your API key as{' '}
            <code>Authorization: Bearer dk_...</code>
          </p>
        </Segment>

        {newKey && (
          <Message positive style={{ borderRadius: '12px' }}>
            <Message.Header>New API Key Generated</Message.Header>
            <p style={{ wordBreak: 'break-all', fontFamily: 'monospace', margin: '0.5em 0' }}>
              {newKey}
            </p>
            <p style={{ color: '#666', fontSize: '0.9em' }}>
              Copy this key now. It won't be shown again.
            </p>
            <Button size="small" positive={copied} onClick={handleCopy}>
              <Icon name={copied ? 'check' : 'copy'} />
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </Message>
        )}

        <Form onSubmit={(e) => { e.preventDefault(); handleGenerate(); }}>
          <Form.Group>
            <Form.Input
              placeholder="Key name (e.g., IFTTT)"
              value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)}
              width={12}
            />
            <Form.Button type="submit" primary width={4} fluid>
              Generate Key
            </Form.Button>
          </Form.Group>
        </Form>

        <List divided relaxed style={{ marginTop: '1em' }}>
          {keys.map(k => (
            <List.Item key={k.id}>
              <List.Content style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <strong>{k.keyPrefix}...</strong>
                  {k.name && <span style={{ marginLeft: '0.75em', color: '#555' }}>{k.name}</span>}
                  <span style={{ marginLeft: '1em', color: '#999', fontSize: '0.85em' }}>
                    Created {formatDate(k.createdAt)}
                  </span>
                  <span style={{ marginLeft: '0.75em', color: '#999', fontSize: '0.85em' }}>
                    Last used {formatDate(k.lastUsedAt)}
                  </span>
                </div>
                <div>
                  {confirmDeleteId === k.id ? (
                    <>
                      <Button size="mini" negative onClick={() => handleDelete(k.id)}>Confirm</Button>
                      <Button size="mini" onClick={() => setConfirmDeleteId(null)}>Cancel</Button>
                    </>
                  ) : (
                    <Icon name="trash" link color="red" onClick={() => setConfirmDeleteId(k.id)} />
                  )}
                </div>
              </List.Content>
            </List.Item>
          ))}
          {keys.length === 0 && (
            <List.Item>
              <List.Content>
                <em>No API keys yet. Generate one above.</em>
              </List.Content>
            </List.Item>
          )}
        </List>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>Close</Button>
      </Modal.Actions>
    </StyledModal>
  );
};

export default ApiKeyModal;
